import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Message, translator } from 'mage-i18n';
import { VerticalRhythm, withResponsive } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { ButtonPrimary, Link, Typography, SpacerVertical, View } from '@klarna/bubble-ui';
import { analytics } from 'moet-core';

import { ContainerTemplate, MessagingStep } from '@/components';
import { getSelectedPlatform } from '@/selectors/platforms';
import { getMerchantAwareArrayTranslator } from '@/selectors/merchant';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_MERCHANT_NATIVE_PLATFORM_CONTAINER_IDS } from './constants';

const tPrefix = 'products-fe.containers.otherPlatform';

const strong = value => <strong>{value}</strong>;
strong.displayName = 'strong';

const renderSteps = (steps, tArrayMerchantAware) => (
  <ol style={{ listStyle: 'none', paddingLeft: 0 }}>
    {steps.map((step, index) => (
      <li key={index} style={{ marginBottom: '5px' }}>
        <Typography textToken="text-style/text/labels/tall/regular">
          {tArrayMerchantAware(`${step}.default`).map(({ text, link }, subIndex) =>
            link ? (
              <Link key={subIndex} href={link} target="_blank" rel="noopener noreferrer">
                {text}
              </Link>
            ) : (
              <span key={subIndex}>
                {text.split(/(Klarna)/).map(part => (part === 'Klarna' ? strong(part) : part))}
              </span>
            ),
          )}
        </Typography>
      </li>
    ))}
  </ol>
);

export const NativePlatform = ({ t, platform, isMobile, tArrayMerchantAware }) => {
  const isStripePlatform = platform.name.toLowerCase() === 'stripe';
  const steps = [`${tPrefix}.step1`, `${tPrefix}.step2`, `${tPrefix}.step3`];
  const titleKey = isStripePlatform ? `${tPrefix}.title_stripe` : `${tPrefix}.title`;
  const infoKey = isStripePlatform ? `${tPrefix}.info_stripe` : `${tPrefix}.info2`;
  const buttonKey = isStripePlatform ? `${tPrefix}.link_stripe` : `${tPrefix}.link2`;

  return (
    <ContainerTemplate>
      <MessagingStep title={[t(titleKey)]}>
        <SpacerVertical spaceToken="space/150" />
        <Typography textToken="text-style/text/super-paragraphs/tall/regular">
          <Message path={infoKey} params={{ strong, platform: platform.name }} />
        </Typography>

        {isStripePlatform && (
          <div>
            {renderSteps(steps, tArrayMerchantAware)}
            <Typography textToken="text-style/text/super-paragraphs/tall/regular">
              {tArrayMerchantAware(`${tPrefix}.text_stripe.default`).map(({ text, link }, index) =>
                link ? (
                  <Link key={index} href={link} target="_blank" rel="noopener noreferrer">
                    {text}
                  </Link>
                ) : (
                  <span key={index}>{text}</span>
                ),
              )}
            </Typography>
          </div>
        )}

        <VerticalRhythm spacing={5} />
        <View style={{ alignItems: isMobile ? 'stretch' : 'flex-start' }}>
          <ButtonPrimary
            size="medium"
            onClick={() => {
              analytics.logEvent(ANALYTICS_EVENTS.goToIntegrationGuide, {
                platform: platform.name,
              });
              document.location.href = platform.url;
            }}
            {...generateCyElemId(CY_MERCHANT_NATIVE_PLATFORM_CONTAINER_IDS.BTN_CONTINUE, window)}
          >
            <Message path={buttonKey} params={{ platform: platform.name }} />
          </ButtonPrimary>
        </View>
      </MessagingStep>
    </ContainerTemplate>
  );
};

NativePlatform.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  platform: PropTypes.shape({
    name: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  tArrayMerchantAware: PropTypes.func.isRequired,
};

export default connect(state => ({
  t: translator(state),
  platform: getSelectedPlatform(state),
  tArrayMerchantAware: getMerchantAwareArrayTranslator(state),
}))(withResponsive(NativePlatform));
